import { useToast } from '@shared/hooks/use-toast';
import { Button } from '@shared/components/ui/button';
import { EnterIcon, EscIcon } from '@shared/components/ui/icons';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cart } from '../../models/Cart';

// 🎯 Extender la interfaz del ref para incluir método personalizado
interface ExtendedModalCenterRef extends ModalCenterRef {
  openWithCart: (cart: Cart) => void;
}

const LockCartModal = forwardRef<ExtendedModalCenterRef, {}>((props, ref) => {
  const { t } = useTranslation('cartsModule');
  const { toast } = useToast();

  // 🎯 Referencia interna al ModalCenter para evitar recursión
  const internalModalRef = useRef<ModalCenterRef>(null);

  // 🎯 Estado interno del modal - NO causa re-render del padre
  const [cart, setCart] = useState<Cart | null>(null);

  const resetForm = () => {
    setCart(null);
  };

  const handleCancel = () => {
    internalModalRef.current?.closeModal();
    resetForm();
  };

  const handleLock = () => {
    try {
      if (!cart) {
        toast({
          title: t('errors.generic'),
          description: t('modal.lock.error_no_selection'),
          variant: 'destructive',
        });
        return;
      }

      // TODO: Implementar llamada al endpoint cuando esté disponible
      toast({
        title: t('modal.lock.coming_soon_title'),
        description: t('modal.lock.coming_soon_description'),
        variant: 'default',
      });

      resetForm();
      handleCancel();
    } catch (error: any) {
      toast({
        title: t('modal.lock.error_title'),
        description: t('errors.unexpected'),
        variant: 'destructive',
      });
    }
  };

  // 🎯 ÚNICO useCallback necesario - Se pasa a useImperativeHandle
  const openWithCart = useCallback((cartData: Cart) => {
    setCart(cartData);
    internalModalRef.current?.openModal();
  }, []);

  // 🎯 Exponer métodos personalizados usando useImperativeHandle
  useImperativeHandle(
    ref,
    () => ({
      openModal: () => internalModalRef.current?.openModal(),
      closeModal: () => internalModalRef.current?.closeModal(),
      openWithCart, // ← Método nuevo para uso directo
    }),
    [openWithCart],
  );

  return (
    <ModalCenter ref={internalModalRef} title={t('modal.lock.title')} type="warning">
      <div className="space-y-4">
        <p className="text-sm text-text">{t('modal.lock.description')}</p>
        <p className="text-sm text-text font-medium">
          {t('modal.lock.confirmation')} <strong>{cart?.code}</strong>?
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="lg" onClick={handleCancel} name="cancel" className="w-full sm:w-auto">
            {t('button.cancel')}
            <EscIcon className="ml-1 !h-4 !w-auto" />
          </Button>
          <Button variant="primary" size="lg" name="lock-btn-confirm" onClick={handleLock} className="w-full sm:w-auto">
            {t('button.lock')}
            <EnterIcon className="ml-1 !h-3.5 !w-auto" />
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

export default LockCartModal;
LockCartModal.displayName = 'LockCartModal';
